/* =============================================================================
   VIEWS / ADMIN / MENU-RECIPE-EDITOR — Rezept je Gericht + Live-Nährwerte (S151 A1)
   =============================================================================
   Im Menü-Editor je Gericht: Zutaten zuordnen (Zutat + Menge + Einheit
   [base|benannt] + optional-Flag + Reihenfolge). Live darunter eine Nährwert-Box
   (core/nutrition.js, identische Formel wie die RPC menu_item_nutrition).
   `missing_data` → Hinweis „Nährwerte unvollständig". Hinter Flag `ingredientsDb`.

   Non-destruktiv: Mengen-/Optional-Änderung rechnet nur die Box neu (kein
   Zeilen-Re-Render → Tipp-Fokus bleibt); Zeile hinzufügen/entfernen/Zutat
   wechseln rendert die Zeilenliste neu. Inline-Handler ohne window.func().
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import {
  computeNutrition, ALLERGENS_EU14, DIETARY_TAGS
} from '../../core/nutrition.js';
// S188-2b — entitäts-bewusstes Framing: Wahl-Item (option/extra) → „Zutat"
// (Single-Picker, keine Schritte); Speise → „Rezept" (unverändert).
import { RECIPE_MODE, entityModeForItemType, isIngredientMode, recipeLabelsForItemType, cleanIngredientRow } from '../../core/recipe-entity.js';

const ALLERGEN_LABEL = Object.fromEntries(ALLERGENS_EU14.map(a => [a.key, a.label]));
const TAG_LABEL = Object.fromEntries(DIETARY_TAGS.map(t => [t.key, t.label]));

let _menuItemId = null;
let _ingredients = [];           // alle Zutaten (Dropdown)
let _ingredientsById = {};
let _unitsByIng = {};            // ingredientId → [{unitName, gramsOrMl}]
let _rows = [];                  // Rezept-Draft
let _orderMode = 'auto';         // S154 'auto' (Kategorie-Rang) | 'custom' (step_no 1..n)
let _mode = RECIPE_MODE.RECIPE;  // S188-2b 'recipe' (Speise) | 'ingredient' (Wahl-Item)
let _onRecipeChange = null;      // S155 — Hook für den Konfigurator (Live-Panel)
let _ingredientFilter = null;    // S156c — Constraint-Filter (ing → bool); null = aus

const STATIONS = ['', 'Grill', 'Theke', 'Pass', 'Kalt', 'Fritteuse'];

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }

// -----------------------------------------------------------------------------
// Editor öffnen
// -----------------------------------------------------------------------------
// S155 — Lade-Logik herausgelöst, damit der Konfigurator denselben Rezept-State
// einbetten kann (menuItemId=null für ein neues Gericht → leeres Rezept).
export async function loadRecipeData(menuItemId) {
  _menuItemId = menuItemId || null;
  // S188-2b — Framing-Modus aus dem itemType des Menü-Items (null = neues Gericht → recipe).
  const _mi = menuItemId
    ? ((storage()._getInternalState && (storage()._getInternalState().menu || []).find(m => m.id === menuItemId)) || null)
    : null;
  _mode = entityModeForItemType(_mi && _mi.itemType);
  let rows = [];
  try {
    [_ingredients, rows] = await Promise.all([
      storage().ingredients.list(),
      menuItemId ? storage().recipes.listFor(menuItemId) : Promise.resolve([])
    ]);
  } catch (e) {
    console.warn('[recipe] load:', e);
    _ingredients = []; rows = [];
  }
  _ingredientsById = Object.fromEntries(_ingredients.map(i => [i.id, i]));
  // benannte Einheiten für ALLE Zutaten vorladen (Live-Box rechnet sync)
  _unitsByIng = {};
  try {
    const all = await Promise.all(_ingredients.map(i => storage().ingredientUnits.listFor(i.id).then(u => [i.id, u || []])));
    _unitsByIng = Object.fromEntries(all);
  } catch (_) { /* tolerant */ }

  _rows = (rows || []).map(r => ({
    ingredientId: r.ingredientId, quantity: r.quantity, unit: r.unit || 'base',
    isOptional: !!r.isOptional, sortOrder: r.sortOrder || 0,
    stepNo: (r.stepNo != null ? r.stepNo : null), stepText: r.stepText || '', station: r.station || ''
  }));
  _orderMode = _rows.some(r => r.stepNo != null) ? 'custom' : 'auto';
  if (_orderMode === 'custom') _rows.sort((a, b) => (a.stepNo || 0) - (b.stepNo || 0) || (a.sortOrder || 0) - (b.sortOrder || 0));
  // S188-2b — im Zutat-Modus GENAU EINE Zeile: Bestand auf die erste klemmen
  // (Daten: alle verknüpften Optionen hängen an genau 1 Zutat), sonst eine leere
  // Anzeige-Zeile für den Single-Picker seeden. Keine Schritte/Stationen.
  if (isIngredientMode(_mode)) {
    _orderMode = 'auto';
    _rows = _rows.slice(0, 1).map(r => ({ ...r, stepNo: null, stepText: '', station: '' }));
    if (_rows.length === 0) _rows = [{ ingredientId: '', quantity: null, unit: 'base', isOptional: false, sortOrder: 0 }];
  }
}

/** Setzt den Change-Hook (Konfigurator-Live-Panel). null zum Lösen. */
export function setRecipeChangeHook(fn) { _onRecipeChange = (typeof fn === 'function') ? fn : null; }

/**
 * S156c — Constraint-Filter für die Zutaten-Dropdowns (Konfigurator-Ziel-Check).
 * `fn(ingredient)` → true = konform/anbieten. null = Filter aus. Non-destruktiv:
 * bereits gewählte verletzende Zutaten bleiben (rot markiert), nur die AUSWAHL
 * ist gefiltert. Aufrufer rendert danach neu (rerenderShell).
 */
export function setIngredientFilter(fn) { _ingredientFilter = (typeof fn === 'function') ? fn : null; }

/** S156 — Rezeptzeilen extern setzen (generativer Konfigurator-Entwurf). */
export function setRecipeRows(rows) {
  _rows = (Array.isArray(rows) ? rows : []).map(r => ({
    ingredientId: r.ingredientId, quantity: r.quantity != null ? r.quantity : null, unit: r.unit || 'base',
    isOptional: !!r.isOptional, sortOrder: r.sortOrder || 0,
    stepNo: (r.stepNo != null ? r.stepNo : null), stepText: r.stepText || '', station: r.station || ''
  }));
  _orderMode = 'auto';
}

/** Aktuelle Zutaten-Liste (für den Konfigurator, z. B. „keine Zutaten"-Hinweis). */
export function recipeIngredientCount() { return _ingredients.length; }

/** Innenteil des Rezept-Editors (ohne Modal-Header/Save-Button) für Einbettung. */
export function renderRecipeInnerHtml(opts = {}) {
  const withBox = opts.withBox !== false;
  if (_ingredients.length === 0) {
    return `<div class="hint" style="padding:12px; background:var(--warn-soft,#fff4e0); border-radius:6px;">Noch keine Zutaten angelegt. Erst im Tab „Zutaten" Zutaten anlegen, dann hier zuordnen.</div>`;
  }
  // S188-2b — Wahl-Item: Single-Zutat-Picker, keine „+ Zutat"/Schritte/Stationen.
  if (isIngredientMode(_mode)) {
    return `
      <div id="recipe-rows">${renderSingleIngredientHtml()}</div>
      ${withBox ? `<div id="recipe-nutrition" style="margin-top:16px;">${renderNutritionBoxHtml(computeBox())}</div>` : ''}`;
  }
  // Speise: Rezept (mehrere Zutaten + Bauanleitung) — unverändert.
  return `
    <div id="recipe-rows">${renderRowsHtml()}</div>
    <button class="btn btn-secondary btn-sm" style="margin-top:8px;" onclick="recipeAddRow()">+ Zutat hinzufügen</button>
    ${withBox ? `<div id="recipe-nutrition" style="margin-top:16px;">${renderNutritionBoxHtml(computeBox())}</div>` : ''}
    <div id="recipe-build" style="margin-top:16px;">${renderBuildAuthoringHtml()}</div>`;
}

/** S188-2b — prominenter Single-Zutat-Picker für ein Wahl-Item (genau EINE Zutat). */
function renderSingleIngredientHtml() {
  const r = _rows[0] || { ingredientId: '', quantity: null, unit: 'base' };
  const ing = _ingredientsById[r.ingredientId];
  const units = _unitsByIng[r.ingredientId] || [];
  const baseLabel = ing ? (ing.baseUnit === 'piece' ? 'Stück' : ing.baseUnit) : 'base';
  const filterOn = typeof _ingredientFilter === 'function';
  const opts = _ingredients.filter(o => !filterOn || _ingredientFilter(o) || o.id === r.ingredientId);
  const jump = ing
    ? ` · <a href="#" onclick="recipeJumpToIngredient('${htmlEscape(ing.id)}'); return false;">↗ Zutat in der Warenwirtschaft öffnen</a>`
    : '';
  return `
    <div class="mre-single">
      <label class="mre-single-label">Zutat aus der Warenwirtschaft (Single-Source)</label>
      <div class="mre-single-row">
        <select class="mre-single-pick" onchange="recipeRowSet(0,'ingredientId', this.value)">
          <option value="">— Zutat wählen —</option>
          ${opts.map(o => `<option value="${htmlEscape(o.id)}" ${o.id === r.ingredientId ? 'selected' : ''}>${htmlEscape(o.name)}</option>`).join('')}
        </select>
        <span class="mre-single-qty">Menge <input type="number" step="0.1" min="0" value="${r.quantity != null ? r.quantity : ''}" oninput="recipeRowSetQty(0, this.value)"></span>
        <select class="mre-single-unit" onchange="recipeRowSet(0,'unit', this.value)">
          <option value="base" ${r.unit === 'base' ? 'selected' : ''}>${htmlEscape(baseLabel)} (Basis)</option>
          ${units.map(u => `<option value="${htmlEscape(u.unitName)}" ${r.unit === u.unitName ? 'selected' : ''}>${htmlEscape(u.unitName)}</option>`).join('')}
        </select>
      </div>
      <p class="hint" style="margin:6px 0 0; font-size:.8rem;">Nährwerte &amp; Allergene kommen aus der Zutat — hier nicht doppelt pflegen. Menge/Einheit optional.${jump}</p>
    </div>`;
}

export async function openRecipeEditor(menuItemId) {
  if (!flagOn()) return;
  setRecipeChangeHook(null);
  const overlay = document.getElementById('modal-overlay');
  const content = document.getElementById('modal-content');
  if (!content || !overlay) return;
  content.innerHTML = `<div class="modal-header"><h2>Lade…</h2><button class="modal-close" onclick="closeModal()">×</button></div><p class="hint">Lade…</p>`;
  overlay.classList.add('active');
  await loadRecipeData(menuItemId);
  content.innerHTML = renderEditorHtml();
}

function renderEditorHtml() {
  const mi = (storage()._getInternalState && (storage()._getInternalState().menu || []).find(m => m.id === _menuItemId)) || {};
  const L = recipeLabelsForItemType(mi.itemType);   // S188-2b — „Zutat" vs „Rezept"
  return `
    <div class="modal-header">
      <h2>${L.icon} ${htmlEscape(L.title)} · ${htmlEscape(mi.name || '')}</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    ${renderRecipeInnerHtml()}
    ${_ingredients.length === 0 ? '' : `
    <div class="btn-row" style="margin-top:16px;">
      <button class="btn btn-primary" onclick="saveRecipe()">${htmlEscape(L.save)}</button>
      <button class="btn btn-ghost" onclick="closeModal()">Abbrechen</button>
    </div>`}`;
}

function renderRowsHtml() {
  if (!_rows.length) return `<div class="hint" style="padding:8px 0;">Noch keine Zutaten im Rezept.</div>`;
  return `
    <table class="order-modal-items" style="width:100%;">
      <thead><tr>
        <th>Zutat</th><th style="width:90px;">Menge</th><th style="width:130px;">Einheit</th>
        <th style="width:80px;" title="abwählbar (zählt nicht in die Nährwerte)">Optional</th><th style="width:40px;"></th>
      </tr></thead>
      <tbody>
        ${_rows.map((r, i) => renderRowHtml(r, i)).join('')}
      </tbody>
    </table>`;
}

function renderRowHtml(r, i) {
  const ing = _ingredientsById[r.ingredientId];
  const units = _unitsByIng[r.ingredientId] || [];
  const baseLabel = ing ? (ing.baseUnit === 'piece' ? 'Stück' : ing.baseUnit) : 'base';
  // S156c — Auswahl auf konforme Zutaten filtern; gewählte (auch verletzende) immer
  // anbieten (non-destruktiv). Verletzende gewählte Zeile rot markieren.
  const filterOn = typeof _ingredientFilter === 'function';
  const violates = filterOn && ing && !_ingredientFilter(ing);
  const opts = _ingredients.filter(o => !filterOn || _ingredientFilter(o) || o.id === r.ingredientId);
  return `
    <tr ${violates ? 'style="background:var(--danger-soft,#fbe3e3);"' : ''}>
      <td>
        <select onchange="recipeRowSet(${i},'ingredientId', this.value)" ${violates ? 'style="border-color:var(--danger,#b94a48);"' : ''} ${violates ? 'title="Verstößt gegen das aktive Ziel (Diät-Constraint)"' : ''}>
          <option value="">— wählen —</option>
          ${opts.map(opt => `<option value="${htmlEscape(opt.id)}" ${opt.id === r.ingredientId ? 'selected' : ''}>${htmlEscape(opt.name)}</option>`).join('')}
        </select>
        ${violates ? '<span class="hint" style="color:var(--danger,#b94a48); font-size:.72rem; display:block;">⚠︎ Verstoß gegen Ziel</span>' : ''}
      </td>
      <td><input type="number" step="0.1" min="0" value="${r.quantity != null ? r.quantity : ''}" oninput="recipeRowSetQty(${i}, this.value)" style="width:80px;"></td>
      <td>
        <select onchange="recipeRowSet(${i},'unit', this.value)">
          <option value="base" ${r.unit === 'base' ? 'selected' : ''}>${htmlEscape(baseLabel)} (Basis)</option>
          ${units.map(u => `<option value="${htmlEscape(u.unitName)}" ${r.unit === u.unitName ? 'selected' : ''}>${htmlEscape(u.unitName)}</option>`).join('')}
        </select>
      </td>
      <td style="text-align:center;"><input type="checkbox" ${r.isOptional ? 'checked' : ''} onchange="recipeRowSetOptional(${i}, this.checked)"></td>
      <td><button class="btn btn-ghost btn-xs" style="color:var(--danger,#b94a48);" onclick="recipeRemoveRow(${i})">✕</button></td>
    </tr>`;
}

// -----------------------------------------------------------------------------
// Live-Nährwert-Box
// -----------------------------------------------------------------------------
function computeBox() {
  return computeNutrition(_rows, _ingredientsById, _unitsByIng);
}

export function renderNutritionBoxHtml(n) {
  if (!n) return '';
  const big7 = [
    ['Energie', `${n.energyKcal} kcal` + (n.energyKj ? ` / ${n.energyKj} kJ` : '')],
    ['Fett', `${n.fatG} g`], ['… ges. Fettsäuren', `${n.fatSaturatedG} g`],
    ['Kohlenhydrate', `${n.carbsG} g`], ['… Zucker', `${n.sugarG} g`],
    ['Eiweiß', `${n.proteinG} g`], ['Salz', `${n.saltG} g`]
  ];
  const allerg = (n.allergens || []).map(a => `<span class="med-tag-chip" style="font-size:.78rem;">${htmlEscape(ALLERGEN_LABEL[a] || a)}</span>`).join(' ');
  const tags = (n.dietaryTags || []).map(t => `<span class="med-tag-chip active" style="font-size:.78rem;">${htmlEscape(TAG_LABEL[t] || t)}</span>`).join(' ');
  return `
    <div style="border:1px solid var(--border,#e2e2e2); border-radius:8px; padding:12px 14px; background:var(--bg-soft,#f6f6f6);">
      <div style="display:flex; justify-content:space-between; align-items:center; gap:10px; flex-wrap:wrap;">
        <strong>Nährwerte je Portion ${n.ingredientCount ? `<span class="hint" style="font-weight:normal;">(${n.ingredientCount} Zutat${n.ingredientCount === 1 ? '' : 'en'})</span>` : ''}</strong>
        ${n.missingData ? `<span style="color:var(--danger,#b94a48); font-size:.82rem;">⚠️ Nährwerte unvollständig</span>` : ''}
      </div>
      <div style="display:grid; grid-template-columns:repeat(2,1fr); gap:2px 16px; margin-top:8px; font-size:.88rem;">
        ${big7.map(([k, v]) => `<div style="display:flex; justify-content:space-between;"><span class="hint">${k}</span><span style="font-weight:600;">${htmlEscape(v)}</span></div>`).join('')}
      </div>
      ${allerg ? `<div style="margin-top:8px;"><span class="hint" style="font-size:.78rem;">Allergene:</span> ${allerg}</div>` : ''}
      ${tags ? `<div style="margin-top:4px;"><span class="hint" style="font-size:.78rem;">Tags:</span> ${tags}</div>` : ''}
    </div>`;
}

function refreshBox() {
  const box = document.getElementById('recipe-nutrition');
  if (box) box.innerHTML = renderNutritionBoxHtml(computeBox());
  if (_onRecipeChange) { try { _onRecipeChange(); } catch (_) {} } // S155 Konfigurator-Live-Panel
}
function refreshRows() {
  const wrap = document.getElementById('recipe-rows');
  if (wrap) wrap.innerHTML = isIngredientMode(_mode) ? renderSingleIngredientHtml() : renderRowsHtml();
  refreshBox();
  if (!isIngredientMode(_mode)) refreshBuild();   // Wahl-Item hat keine Bauanleitung
}
function refreshBuild() {
  const wrap = document.getElementById('recipe-build');
  if (wrap) wrap.innerHTML = renderBuildAuthoringHtml();
}

// -----------------------------------------------------------------------------
// S154 — Bauanleitung-Authoring (Reihenfolge ↑↓ + Schritt-Text + Station)
// -----------------------------------------------------------------------------
export function renderBuildAuthoringHtml() {
  const rows = _rows.filter(r => r.ingredientId);
  if (!rows.length) return '';
  const modeLabel = _orderMode === 'custom' ? 'Eigene Reihenfolge (step_no)' : 'Automatisch (Kategorie-Reihenfolge)';
  const list = _rows.map((r, i) => {
    if (!r.ingredientId) return '';
    const ing = _ingredientsById[r.ingredientId];
    const name = ing ? ing.name : '—';
    return `
      <li style="display:flex; gap:6px; align-items:center; padding:3px 0;">
        <span style="display:flex; flex-direction:column;">
          <button class="btn btn-ghost btn-xs" title="nach oben" onclick="recipeMoveRow(${i},'up')">↑</button>
          <button class="btn btn-ghost btn-xs" title="nach unten" onclick="recipeMoveRow(${i},'down')">↓</button>
        </span>
        <span style="min-width:120px; font-weight:600;">${htmlEscape(name)}</span>
        <input type="text" value="${htmlEscape(r.stepText || '')}" placeholder="Schritt-Text (optional, sonst automatisch)"
               style="flex:1;" oninput="recipeRowStepText(${i}, this.value)">
        <select onchange="recipeRowStation(${i}, this.value)" style="width:110px;" title="Station">
          ${STATIONS.map(s => `<option value="${htmlEscape(s)}" ${r.station === s ? 'selected' : ''}>${s || '— Station —'}</option>`).join('')}
        </select>
      </li>`;
  }).join('');
  return `
    <div style="border:1px solid var(--border,#e2e2e2); border-radius:8px; padding:12px 14px;">
      <div style="display:flex; justify-content:space-between; align-items:center; gap:10px; flex-wrap:wrap;">
        <strong>🔨 Bauanleitung / Reihenfolge</strong>
        <span style="display:flex; gap:8px; align-items:center;">
          <span class="hint" style="font-size:.8rem;">${modeLabel}</span>
          <button class="btn btn-ghost btn-sm" onclick="recipeAutoOrder()" ${_orderMode === 'custom' ? '' : 'disabled'}>↺ Auto-Reihenfolge</button>
        </span>
      </div>
      <p class="hint" style="margin:4px 0 8px;">↑↓ legt eine feste Schrittfolge fest (step_no). Schritt-Text ist optional je Zutat (leer = automatisch). Station optional. KDS/Theke zeigen genau diese Anleitung.</p>
      <ol style="margin:0; padding-left:18px;">${list}</ol>
    </div>`;
}

export function recipeMoveRow(i, dir) {
  const swap = dir === 'up' ? i - 1 : i + 1;
  if (i < 0 || i >= _rows.length || swap < 0 || swap >= _rows.length) return;
  [_rows[i], _rows[swap]] = [_rows[swap], _rows[i]];
  _orderMode = 'custom'; // manuelle Reihenfolge → step_no wird beim Speichern gesetzt
  refreshRows();
}
export function recipeRowStepText(i, val) {
  if (!_rows[i]) return;
  _rows[i].stepText = val; // oninput → KEIN Re-Render (Fokus bleibt)
}
export function recipeRowStation(i, val) {
  if (!_rows[i]) return;
  _rows[i].station = val || '';
}
export function recipeAutoOrder() {
  _orderMode = 'auto';
  _rows.forEach(r => { r.stepNo = null; r.stepText = ''; });
  refreshRows();
}

// -----------------------------------------------------------------------------
// Row-Handler
// -----------------------------------------------------------------------------
export function recipeAddRow() {
  if (isIngredientMode(_mode)) return;   // Wahl-Item: genau EINE Zutat (kein Hinzufügen)
  _rows.push({ ingredientId: '', quantity: null, unit: 'base', isOptional: false, sortOrder: _rows.length });
  refreshRows();
}

/** S188-2b — Sprung zur verknüpften Zutat in der Warenwirtschaft (SSOT). */
export function recipeJumpToIngredient(id) {
  if (typeof window.closeModal === 'function') window.closeModal();
  if (typeof window.switchTab === 'function') window.switchTab('ingredients');
  if (id && typeof window.ingredientsFocus === 'function') { try { window.ingredientsFocus(id); } catch (_) { /* optional */ } }
}
export function recipeRemoveRow(i) { _rows.splice(i, 1); refreshRows(); }
export async function recipeRowSet(i, field, value) {
  if (!_rows[i]) return;
  _rows[i][field] = value;
  if (field === 'ingredientId') {
    _rows[i].unit = 'base'; // Einheit zurücksetzen (neue Zutat → andere Einheiten)
    // Einheiten der neuen Zutat sicherstellen (falls noch nicht geladen)
    if (value && !_unitsByIng[value]) {
      try { _unitsByIng[value] = await storage().ingredientUnits.listFor(value) || []; } catch (_) { _unitsByIng[value] = []; }
    }
    refreshRows();
  } else {
    refreshBox();
  }
}
export function recipeRowSetQty(i, value) {
  if (!_rows[i]) return;
  _rows[i].quantity = (value === '' || value == null) ? null : Number(value);
  refreshBox(); // nur Box → Tipp-Fokus bleibt erhalten
}
export function recipeRowSetOptional(i, on) {
  if (!_rows[i]) return;
  _rows[i].isOptional = !!on;
  refreshBox();
}

// -----------------------------------------------------------------------------
// Speichern
// -----------------------------------------------------------------------------
// S155 — bereinigte Rezeptzeilen (Pflichtfelder, Schrittfolge) ohne Storage-Call,
// damit der Konfigurator sie zusammen mit dem menu_item speichern kann.
export function getRecipeCleanRows() {
  return _rows
    .filter(r => r.ingredientId && r.quantity != null && isFinite(r.quantity))
    .map((r, i) => ({
      ingredientId: r.ingredientId, quantity: Number(r.quantity), unit: r.unit || 'base',
      isOptional: !!r.isOptional, sortOrder: i,
      stepNo: (_orderMode === 'custom') ? (i + 1) : null,
      stepText: (r.stepText && r.stepText.trim()) ? r.stepText.trim() : null,
      station: r.station || null
    }));
}

export async function saveRecipe() {
  // S188-2b-Fix — Zutat-Modus hat eine EIGENE Bereinigung: die Menge ist optional
  // (Nährwerte kommen aus der Zutat), also NICHT über getRecipeCleanRows filtern
  // (das verwarf Zeilen ohne Menge → Neu-Verknüpfen schrieb nichts, meldete aber
  // Erfolg). row=null → „entfernen" (setItems bekommt [] und löscht die Zeile).
  const ingMode = isIngredientMode(_mode);
  const row = ingMode ? cleanIngredientRow(_rows[0]) : null;
  const clean = ingMode ? (row ? [row] : []) : getRecipeCleanRows();
  try {
    // setItems ersetzt alle recipe_items des Items (delete-all → insert) → genau eine
    // Zeile im Zutat-Modus, kein Duplikat; leeres clean = Zeile entfernt.
    const result = await storage().recipes.setItems(_menuItemId, clean);
    // Kein Fake-Erfolg: war eine Zutat gewählt, muss auch eine Zeile zurückkommen.
    if (ingMode && row && (!Array.isArray(result) || result.length === 0)) {
      showToast('Speichern fehlgeschlagen — bitte erneut versuchen.', 'error');
      return; // Modal offen lassen
    }
    // S152 A2 — Nährwert-Cache (KDS/Order-Detail) verwerfen → frische Komposition.
    if (window.App && window.App.nutritionCache) window.App.nutritionCache.invalidate();
    showToast(ingMode ? (row ? 'Zutat gespeichert' : 'Zutat entfernt') : 'Rezept gespeichert', 'success');
    if (typeof window.closeModal === 'function') window.closeModal();
    // Badge in der aufrufenden Liste (Optionssätze/Menü-Editor) non-destruktiv nachziehen.
    renderRecipeNutritionInto(_menuItemId);
  } catch (e) {
    console.error('[recipe] save:', e);
    showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// -----------------------------------------------------------------------------
// Nährwert-Badge im Menü-Editor (RPC-basiert, je Gericht)
// -----------------------------------------------------------------------------
export async function renderRecipeNutritionInto(menuItemId) {
  if (!flagOn()) return;
  const host = document.getElementById(`recipe-nutri-badge-${menuItemId}`);
  if (!host) return;
  // S188-2b — Framing nach itemType (Wahl-Item „Zutat" vs Speise „Rezept").
  const mi = (storage()._getInternalState && (storage()._getInternalState().menu || []).find(m => m.id === menuItemId)) || {};
  const L = recipeLabelsForItemType(mi.itemType);
  const ingMode = entityModeForItemType(mi.itemType) === RECIPE_MODE.INGREDIENT;
  host.innerHTML = `<span class="hint" style="font-size:.8rem;">Nährwerte…</span>`;
  try {
    const n = await storage().nutrition.forMenuItem(menuItemId);
    if (!n || !n.ingredientCount) {
      host.innerHTML = `<span class="hint" style="font-size:.8rem;">${htmlEscape(L.noLink)}</span>`;
      return;
    }
    // Wahl-Item mit verknüpfter, aber ungepflegter Zutat → dezenter Pfleg-Hinweis
    // statt „0 kcal" (macht die Datenlücke sichtbar, z. B. bei Pattys).
    if (ingMode && (n.missingData || !n.energyKcal)) {
      host.innerHTML = `<span class="hint" style="font-size:.8rem; color:var(--danger,#b94a48);">⚠️ ${htmlEscape(L.noNutrition)}</span>`;
      return;
    }
    const allerg = (n.allergens || []).map(a => htmlEscape(ALLERGEN_LABEL[a] || a)).join(', ');
    host.innerHTML = `
      <span style="font-weight:600;">${n.energyKcal} kcal</span>
      <span class="hint" style="font-size:.8rem;">· F ${n.fatG} · KH ${n.carbsG} · E ${n.proteinG} · Salz ${n.saltG} g</span>
      ${n.missingData ? `<span style="color:var(--danger,#b94a48); font-size:.78rem;"> · ⚠️ unvollständig</span>` : ''}
      ${allerg ? `<div class="hint" style="font-size:.76rem; margin-top:2px;">Allergene: ${allerg}</div>` : ''}`;
  } catch (e) {
    host.innerHTML = `<span class="hint" style="font-size:.8rem;">Nährwerte nicht verfügbar.</span>`;
  }
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    openRecipeEditor, recipeAddRow, recipeRemoveRow, recipeRowSet, recipeRowSetQty,
    recipeRowSetOptional, saveRecipe, renderRecipeNutritionInto,
    // S154 Bauanleitung-Authoring
    recipeMoveRow, recipeRowStepText, recipeRowStation, recipeAutoOrder,
    // S188-2b — Sprung zur Zutat (Warenwirtschaft)
    recipeJumpToIngredient
  });
}
